import Head from 'next/head';
import { GetStaticPropsContext } from 'next';
import { useRouter } from 'next/router';
import { getGalleryItems } from 'services/gallery';
import { GalleryItemWithBundle, GalleryItemImage } from 'types';
import LightBoxItem from 'partials/LightBoxItem';
import LightboxLayout from 'layout/LightboxLayout';
import CloseButton from 'components/CloseButton';
import { withPageStaticProps } from 'utils/header';

type GalleryPostParams = {
  id: string;
};

type GalleryPostProps = {
  post: GalleryItemWithBundle;
};

const getTagline = (post: GalleryItemWithBundle) => {
  if (post.photoCredit) {
    return `${post.bundle.display_name} by ${post.photoCredit}`;
  }

  return post.bundle.display_name;
};

const getAspectRatio = (image: GalleryItemImage) => {
  const { height, width } = image.asset.metadata!.dimensions!;

  return width! / height!;
};

const getTitle = (post: GalleryItemWithBundle) => `${getTagline(post)} | Generation Tux Gallery`;

const getAltText = (post: GalleryItemWithBundle) => {
  if (post.image.alt) {
    return post.image.alt;
  }

  return getTagline(post);
};

export const getStaticPaths = async () => {
  const galleryItems = await getGalleryItems();

  const paths = galleryItems.map((item) => {
    return {
      params: {
        id: item._id,
      },
    };
  });

  return {
    paths,
    fallback: false,
  };
};

export const getStaticProps = withPageStaticProps(async ({ params }: GetStaticPropsContext<GalleryPostParams>) => {
  const galleryItems = await getGalleryItems();

  const post = galleryItems.find((post) => post._id === params?.id);

  if (!post) {
    throw new Error(`Tried to build a page for an ID that did not match a post: ${params?.id}`);
  }

  return {
    props: {
      post,
    },
  };
});

const Post = ({ post }: GalleryPostProps) => {
  const router = useRouter();

  return (
    <>
      <Head>
        <title>{getTitle(post)}</title>
      </Head>

      <div className="container !p-0">
        <div className="relative animate-scaleIn shadow-3">
          <CloseButton onClose={() => router.push(`/gallery`)} className="absolute right-0" />

          <div className="row">
            <div className="col-span-12 m-0 flex sm:col-span-6">
              <div className="w-full">
                <img
                  className="ml-auto max-h-[55vh] xs:max-h-[none] xs:w-full xs:object-cover"
                  style={{
                    display: 'block',
                    aspectRatio: `${getAspectRatio(post.image)}`,
                  }}
                  src={`${post.image.asset.url}?w=800&h=800&fm=jpg&fit=fillmax`}
                  alt={getAltText(post)}
                />
              </div>
            </div>
            <div className="col-span-12 m-0 flex flex-col px-32 pt-32 sm:col-span-6 md:px-64 md:pt-64 lg:px-128">
              <div style={{ flexGrow: 1 }}>
                <h2 className="text-h2-display mb-32">{post.bundle.display_name}</h2>
                <div className="pb-32 md:pb-64">
                  <LightBoxItem lightBoxItem={post} error={false} />
                </div>
              </div>

              <div className="pb-32">{post.photoCredit && <p className="text-sm text-gray">{post.photoCredit}</p>}</div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

Post.layout = LightboxLayout;

export default Post;
